import React, { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import UserTrafficSourceAuditService from '../../services/UserTrafficSourceAudit/user-traffic-source-audit.service';
import {
  ProfessionModel,
  TrafficSourceModel,
  UserContactModel,
  UserTrafficSourceAuditFilterModel
} from '../../models/UserTrafficSourceAudit/user-traffic-source-audit-model';

interface Props {
  onSearch: (filters: UserTrafficSourceAuditFilterModel) => void;
  onReset: () => void;
  totalRecords: number;
  setLoading: (loading: boolean) => void;
}

const UserTrafficSourceAuditSearch: React.FC<Props> = ({ onSearch, onReset, totalRecords, setLoading }) => {
  const toast = useRef<Toast>(null);

  // Filter state
  const [startDate, setStartDate]           = useState<Date | null>(null);
  const [endDate, setEndDate]               = useState<Date | null>(null);
  const [keyword, setKeyword]               = useState('');
  const [specificIssue, setSpecificIssue]   = useState('');
  const [professionId, setProfessionId]     = useState<number | null>(null);
  const [trafficSourceId, setTrafficSourceId] = useState<number | null>(null);
  const [introByUserId, setIntroByUserId]   = useState<number | null>(null);
  const [filterApplied, setFilterApplied]   = useState(false);

  const isFirstMount = useRef(true);

  // Master data for dropdowns
  const [professions, setProfessions] = useState<ProfessionModel[]>([]);
  const [trafficSources, setTrafficSources] = useState<TrafficSourceModel[]>([]);
  const [adminUsers, setAdminUsers]   = useState<UserContactModel[]>([]);

  // Debounce search on name/contact (keyword) change
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (keyword === '' && !filterApplied) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      onSearch({
        startDate:          startDate  ? formatDate(startDate)  : undefined,
        endDate:            endDate    ? formatDate(endDate)    : undefined,
        keyword:            keyword.trim()     || undefined,
        keywordSearch:      keyword.trim() || undefined,
        trafficSourceId:    trafficSourceId ?? undefined,
        specificIssue:      specificIssue.trim() || undefined,
        professionId:       professionId  ?? undefined,
        introductionDoneBy: introByUserId ?? undefined,
        pageNumber: 1,
        pageSize: 10
      });
      setFilterApplied(true);
    }, 450);

    return () => clearTimeout(delayDebounce);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  useEffect(() => {
    const load = async () => {
      try {
        const [prof, traffic, admins] = await Promise.all([
          UserTrafficSourceAuditService.getProfessions(),
          UserTrafficSourceAuditService.getTrafficSources(),
          UserTrafficSourceAuditService.getAdminUsers()
        ]);
        setProfessions(prof);
        setTrafficSources(traffic);
        setAdminUsers(admins as any);
      } catch (err) {
        console.error('Error loading filter dropdowns:', err);
      }
    };
    load();
  }, []);

  const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleSearch = () => {
    if (startDate && endDate && endDate < startDate) {
      toast.current?.show({
        severity: 'warn', summary: 'Validation Error',
        detail: 'End Date cannot be earlier than Start Date.', life: 3000
      });
      return;
    }
    onSearch({
      startDate:          startDate  ? formatDate(startDate)  : undefined,
      endDate:            endDate    ? formatDate(endDate)    : undefined,
      keyword:            keyword.trim()     || undefined,
      keywordSearch:      keyword.trim() || undefined,
      trafficSourceId:    trafficSourceId ?? undefined,
      specificIssue:      specificIssue.trim() || undefined,
      professionId:       professionId  ?? undefined,
      introductionDoneBy: introByUserId ?? undefined,
      pageNumber: 1,
      pageSize: 10
    });
    setFilterApplied(true);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setKeyword('');
    setSpecificIssue('');
    setProfessionId(null);
    setTrafficSourceId(null);
    setIntroByUserId(null);
    setFilterApplied(false);
    onReset();
  };

  return (
    <div className="utsa-search-container surface-card p-3 shadow-2 border-round-xl mt-3" style={{
      border: '1px solid rgba(76, 175, 80, 0.15)',
      background: '#ffffff'
    }}>
      <Toast ref={toast} />

      <style>{`
        .utsa-search-container .search-panel-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
            padding-bottom: 0.75rem;
            border-bottom: 1px solid #f0f0f0;
        }

        .utsa-search-container .search-panel-header i {
            color: #4CAF50;
            font-size: 1rem;
        }

        .utsa-search-container .search-panel-header span {
            font-weight: 600;
            color: #374151;
            font-size: 0.9rem;
        }

        .utsa-search-container .p-dropdown,
        .utsa-search-container .p-calendar,
        .utsa-search-container .p-inputtext {
            height: 34px !important;
            min-height: 34px !important;
            font-size: 0.8125rem !important;
            border-radius: 8px !important;
            border-color: #e0e0e0 !important;
            width: 100% !important;
        }

        .utsa-search-container .p-dropdown .p-dropdown-label {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.8125rem !important;
        }

        .utsa-search-container .p-dropdown-trigger {
            width: 2rem !important;
        }

        .utsa-search-container .p-calendar .p-inputtext {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.8125rem !important;
        }

        .utsa-search-container .p-button {
            height: 34px !important;
            min-height: 34px !important;
            font-size: 0.8125rem !important;
        }

        .utsa-search-container .p-dropdown:hover,
        .utsa-search-container .p-calendar:hover,
        .utsa-search-container .p-inputtext:hover {
            border-color: #4CAF50 !important;
        }

        .utsa-search-container .p-dropdown:focus-within,
        .utsa-search-container .p-calendar:focus-within,
        .utsa-search-container .p-inputtext:focus {
            border-color: #4CAF50 !important;
            box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1) !important;
        }

        .utsa-search-dropdown-panel.p-dropdown-panel {
            border-radius: 12px !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
            border: 1px solid #f0f0f0 !important;
            padding: 0.5rem !important;
        }

        .utsa-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
            border-radius: 8px !important;
            margin: 2px 0 !important;
            padding: 0.6rem 1rem !important;
            font-size: 0.875rem !important;
            transition: all 0.2s ease !important;
        }

        .utsa-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
            background: #4CAF50 !important;
            color: #ffffff !important;
        }

        .utsa-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
            background: #4CAF50 !important;
            color: #ffffff !important;
            font-weight: 600 !important;
        }

        .utsa-search-container .search-button-primary {
            background: #4CAF50 !important;
            border: none !important;
        }
        .utsa-search-container .search-button-primary:hover {
            background: #45a049 !important;
        }

        .utsa-search-container .search-button-secondary {
            background: transparent !important;
            border: 1px solid #4CAF50 !important;
            color: #4CAF50 !important;
        }
        .utsa-search-container .search-button-secondary:hover {
            background: rgba(76, 175, 80, 0.05) !important;
            color: #45a049 !important;
            border-color: #45a049 !important;
        }

        .utsa-search-container .compact-grid {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 0.75rem !important;
            align-items: center !important;
        }

        .utsa-search-container .compact-grid > div {
            flex: 0 0 auto !important;
        }

        /* Specific widths for desktop */
        .utsa-search-container .keyword-filter { width: 160px !important; }
        .utsa-search-container .profession-filter { width: 160px !important; }
        .utsa-search-container .intro-filter { width: 160px !important; }
        .utsa-search-container .issue-filter { width: 160px !important; }
        .utsa-search-container .date-filter { width: 130px !important; }
        .utsa-search-container .button-group { width: auto !important; }

        @media screen and (max-width: 1200px) {
            .utsa-search-container .keyword-filter,
            .utsa-search-container .profession-filter,
            .utsa-search-container .intro-filter,
            .utsa-search-container .issue-filter,
            .utsa-search-container .date-filter {
                width: calc(33.33% - 0.5rem) !important;
                flex: 1 1 auto !important;
            }
        }

        @media screen and (max-width: 768px) {
            .utsa-search-container .keyword-filter,
            .utsa-search-container .profession-filter,
            .utsa-search-container .intro-filter,
            .utsa-search-container .issue-filter,
            .utsa-search-container .date-filter {
                width: calc(50% - 0.5rem) !important;
            }
            .utsa-search-container .button-group {
                width: 100% !important;
                justify-content: flex-end;
            }
        }

        @media screen and (max-width: 480px) {
            .utsa-search-container .keyword-filter,
            .utsa-search-container .profession-filter,
            .utsa-search-container .intro-filter,
            .utsa-search-container .issue-filter,
            .utsa-search-container .date-filter {
                width: 100% !important;
            }
        }
      `}</style>

      {/* Search Panel Header */}
      <div className="search-panel-header">
        <i className="pi pi-filter-fill"></i>
        <span>Filter Records</span>
      </div>

      <div className="compact-grid">
        {/* Keyword Search */}
        <div className="keyword-filter">
          <InputText
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search Name/Contact"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>

        {/* Profession Dropdown */}
        <div className="profession-filter">
          <Dropdown
            value={professionId}
            options={professions}
            optionLabel="professionName"
            optionValue="id"
            placeholder="Profession"
            onChange={(e) => setProfessionId(e.value)}
            filter
            filterBy="professionName"
            showClear
            emptyMessage="No professions found"
            panelClassName="utsa-search-dropdown-panel"
          />
        </div>

        {/* How They Came Dropdown */}
        <div className="profession-filter">
          <Dropdown
            value={trafficSourceId}
            options={trafficSources}
            optionLabel="trafficSourceName"
            optionValue="id"
            placeholder="How They Came"
            onChange={(e) => setTrafficSourceId(e.value as number | null)}
            filter
            filterBy="trafficSourceName"
            showClear
            emptyMessage="No sources found"
            panelClassName="utsa-search-dropdown-panel"
          />
        </div>

        {/* Introduction Done By */}
        <div className="intro-filter">
          <Dropdown
            value={introByUserId}
            options={adminUsers}
            optionLabel="userName"
            optionValue="id"
            placeholder="Introduction By"
            onChange={(e) => setIntroByUserId(e.value)}
            filter
            filterBy="userName"
            showClear
            emptyMessage="No admins found"
            panelClassName="utsa-search-dropdown-panel"
          />
        </div>

        {/* Specific Issue */}
        <div className="issue-filter">
          <InputText
            value={specificIssue}
            onChange={(e) => setSpecificIssue(e.target.value)}
            placeholder="Specific Issue"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>

        {/* Start Date */}
        <div className="date-filter">
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate(e.value as Date | null)}
            dateFormat="dd-mm-yy"
            placeholder="Start Date"
            readOnlyInput
            showButtonBar
          />
        </div>

        {/* End Date */}
        <div className="date-filter">
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate(e.value as Date | null)}
            dateFormat="dd-mm-yy"
            placeholder="End Date"
            readOnlyInput
            showButtonBar
          />
        </div>

        {/* Buttons */}
        <div className="button-group flex gap-2">
          <Button
            label="Filter"
            icon="pi pi-filter"
            onClick={handleSearch}
            className="search-button-primary text-white border-round"
          />
          <Button
            label="Reset"
            icon="pi pi-refresh"
            onClick={handleReset}
            className="search-button-secondary border-round"
          />
        </div>
      </div>

      {/* Record count badge */}
      {filterApplied && (
        <div className="mt-3 text-sm" style={{ borderTop: '1px solid #f0f0f0', paddingTop: '0.75rem' }}>
          <span className="text-black font-semibold text-base">
            Total Records Found: {totalRecords}
          </span>
        </div>
      )}
    </div>
  );
};

export default UserTrafficSourceAuditSearch;
